<template>
  <div class="selected-districts-map-preview" v-if="polygons.length">
    <div class="map-container-frame" :style="{ height }">
      <GmapMap
        ref="gmap"
        :center="mapCenter"
        :zoom="zoom"
        :options="mapOptions"
        style="width: 100%; height: 100%"
        @click="onMapClick"
      >
        <GmapMarker
          v-if="markerPosition"
          :position="markerPosition"
          :clickable="false"
          :draggable="true"
          :z-index="10"
          @dragend="onMarkerMoved"
        />
        <gmap-polygon
          v-for="poly in polygons"
          :key="poly.id"
          :paths="poly.path"
          :options="{
            strokeColor: poly.isPrimary ? '#E53935' : '#783D73',
            strokeOpacity: 0.9,
            strokeWeight: poly.isPrimary ? 3 : 2,
            fillColor: poly.isPrimary ? '#E53935' : '#783D73',
            fillOpacity: poly.isPrimary ? 0.32 : 0.18,
            clickable: false,
            zIndex: poly.isPrimary ? 3 : 1,
          }"
        />
      </GmapMap>
    </div>

    <div class="legend">
      <span class="legend-item">
        <i class="legend-swatch legend-swatch-primary"></i>
        {{ lang === "ar" ? "الحي الرئيسي" : "Primary district" }}
      </span>
      <span class="legend-item">
        <i class="legend-swatch legend-swatch-delivery"></i>
        {{ lang === "ar" ? "أحياء التوصيل" : "Delivery districts" }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

// Read-only preview: draws the boundaries of already-selected districts
// (decoded from their `encoded_polygon`) so admins can visually confirm the
// delivery area on Store/Branch create & edit screens. No interaction —
// district selection itself stays in the dropdowns above this component.
// Optionally (when `markerPosition` is passed) it also shows a draggable
// location marker; moving it or clicking the map emits `marker-change`.
export default {
  name: "SelectedDistrictsMapPreview",

  props: {
    markerPosition: {
      // { lat, lng } — omit to render the preview without a marker
      type: Object,
      default: null,
    },
    districts: {
      // Each item: { id, encoded_polygon, is_primary }
      type: Array,
      default: () => [],
    },
    height: {
      type: String,
      default: "360px",
    },
  },

  data() {
    return {
      // Google Maps is lazy-loaded by vue2-google-maps on the first map mount;
      // this component's map only renders once polygons exist, which needs
      // google loaded — so trigger the load here and re-compute when ready.
      googleReady: false,
      zoom: 11,
      mapOptions: {
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        gestureHandling: "greedy",
      },
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),

    polygons() {
      if (
        !this.googleReady ||
        typeof google === "undefined" ||
        !google.maps ||
        !google.maps.geometry
      ) {
        return [];
      }

      return (this.districts || [])
        .filter((d) => d && d.encoded_polygon)
        .map((d) => {
          let path = [];
          try {
            path = google.maps.geometry.encoding
              .decodePath(d.encoded_polygon)
              .map((p) => ({ lat: p.lat(), lng: p.lng() }));
          } catch (e) {
            path = [];
          }
          return { id: d.id, path, isPrimary: !!d.is_primary };
        })
        .filter((poly) => poly.path.length >= 3);
    },

    mapCenter() {
      const allPoints = this.polygons.reduce(
        (acc, poly) => acc.concat(poly.path),
        []
      );
      if (!allPoints.length) {
        return { lat: 24.7136, lng: 46.6753 }; // Riyadh fallback
      }
      const lat =
        allPoints.reduce((sum, p) => sum + p.lat, 0) / allPoints.length;
      const lng =
        allPoints.reduce((sum, p) => sum + p.lng, 0) / allPoints.length;
      return { lat, lng };
    },
  },

  watch: {
    polygons() {
      this.$nextTick(() => this.fitBounds());
    },
    markerPosition(pos) {
      if (!pos || !this.$refs.gmap) return;
      this.$refs.gmap.$mapPromise.then((map) => {
        const bounds = map.getBounds();
        if (bounds && !bounds.contains(new google.maps.LatLng(pos.lat, pos.lng))) {
          map.panTo(pos);
        }
      });
    },
  },

  created() {
    this.$gmapApiPromiseLazy().then(() => {
      this.googleReady = true;
    });
  },

  mounted() {
    this.$nextTick(() => this.fitBounds());
  },

  methods: {
    onMarkerMoved(e) {
      this.$emit("marker-change", { lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    onMapClick(e) {
      if (!this.markerPosition || !e || !e.latLng) return;
      this.onMarkerMoved(e);
    },
    fitBounds() {
      if (!this.$refs.gmap || !this.polygons.length) return;
      this.$refs.gmap.$mapPromise.then((map) => {
        const bounds = new google.maps.LatLngBounds();
        this.polygons.forEach((poly) => {
          poly.path.forEach((p) => {
            bounds.extend(new google.maps.LatLng(p.lat, p.lng));
          });
        });
        map.fitBounds(bounds);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.selected-districts-map-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  .map-container-frame {
    position: relative;
    width: 100%;
    border-radius: 14px;
    overflow: hidden;
    border: 2px solid rgba(120, 61, 115, 0.15);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

    ::v-deep .vue-map-container {
      width: 100%;
      height: 100%;
    }
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 12.5px;
    color: #616161;

    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .legend-swatch {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 3px;

      &.legend-swatch-primary {
        background: #e53935;
      }

      &.legend-swatch-delivery {
        background: #783d73;
      }
    }
  }
}
</style>
